import React, { useState } from 'react';
import api, { isLimitError } from '../../api/api';
import { useCreateContext } from '../../product/CreateContext';
import { parseAiJson } from '../../utils/parseAiJson';
import RepurposeMenu from '../../components/RepurposeMenu';
import { CopyButton, ErrorBanner, Field, GenerateButton, formGrid } from './generatorUi';
import { useCreateFlow } from './flow';

const LENGTHS = {
    short: { label: 'Short · ~600 words', words: 600 },
    medium: { label: 'Medium · ~1,000 words', words: 1000 },
    long: { label: 'Long · ~1,600 words', words: 1600 },
};

const BlogScreen = () => {
    const { product, promptContext, seed } = useCreateContext();
    const [topic, setTopic] = useState(seed?.topic || '');
    const [keyword, setKeyword] = useState('');
    const [length, setLength] = useState('medium');
    const { flow, report } = useCreateFlow();
    // { title, metaDescription, outline, body }: the flow's, so the Cover image and Review steps see it
    const { article } = flow;
    const setArticle = (next) => report('article', { article: next }, Boolean(next?.body?.trim()));
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleGenerate = async () => {
        if (!topic.trim()) {
            setError('Describe what the article is about.');
            return;
        }
        const prompt = `You are a content marketer who writes useful, search-friendly blog articles that convert readers into customers.
Write a blog article about: ${topic.trim()}
${keyword.trim() ? `Target search keyword: ${keyword.trim()} (use it in the title, the first paragraph and one heading, naturally)\n` : ''}Length: about ${LENGTHS[length].words} words.

What you know about the product, who it's for and the brand voice (write for this person, in this voice):
${promptContext}

Lead with the reader's problem, give genuinely practical advice, and mention ${product?.name || 'the product'} where it naturally helps, ending with one clear call to action${product?.offer ? ` that mentions the current offer (${product.offer})` : ''}.
Use markdown for the body: ## headings, short paragraphs, lists where useful. Don't repeat the title in the body.

Respond ONLY with JSON in this shape, no other text:
{"title": "...", "metaDescription": "under 155 characters", "outline": ["heading 1", "heading 2"], "body": "the article in markdown"}`;

        setError('');
        setIsLoading(true);
        try {
            const parsed = parseAiJson(await api.getChatCompletion(prompt));
            if (!parsed?.body) throw new Error('No article in response');
            setArticle({
                title: String(parsed.title || topic).trim(),
                metaDescription: String(parsed.metaDescription || '').trim(),
                outline: Array.isArray(parsed.outline) ? parsed.outline.map(String) : [],
                body: String(parsed.body).trim(),
            });
        } catch (e) {
            if (isLimitError(e)) return;
            setError("Couldn't write the article. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const fullText = article ? `# ${article.title}\n\n${article.body}` : '';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <ErrorBanner message={error} />
            <div className="ls-card" style={{ gap: 16 }}>
                <div className="ls-card-title">Article brief</div>
                <Field label="What's it about?">
                    <textarea
                        className="ls-textarea"
                        rows={2}
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder="e.g. Meal prep for 12-hour shifts"
                    />
                </Field>
                <div style={formGrid}>
                    <Field label="Search keyword (optional)">
                        <input className="ls-input" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="e.g. meal prep for nurses" />
                    </Field>
                    <Field label="Length">
                        <select className="ls-select" value={length} onChange={(e) => setLength(e.target.value)}>
                            {Object.entries(LENGTHS).map(([id, option]) => <option key={id} value={id}>{option.label}</option>)}
                        </select>
                    </Field>
                </div>
                <GenerateButton isLoading={isLoading} onClick={handleGenerate} label={article ? 'Rewrite article' : 'Write article'} loadingLabel="Writing..." secondary={Boolean(article)} />
            </div>

            {article && (
                <div className="ls-card" style={{ gap: 14 }}>
                    <div className="ls-card-header">
                        <span className="ls-card-title">Draft</span>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <RepurposeMenu source={{ contentType: 'Blog article', title: article.title, text: article.body }} exclude={['blog']} />
                            <CopyButton text={fullText} label="Copy markdown" />
                        </div>
                    </div>
                    <Field label="Title">
                        <input className="ls-input" value={article.title} onChange={(e) => setArticle({ ...article, title: e.target.value })} />
                    </Field>
                    <Field label="Meta description" hint={`${article.metaDescription.length} / 155 characters`}>
                        <input className="ls-input" value={article.metaDescription} onChange={(e) => setArticle({ ...article, metaDescription: e.target.value })} />
                    </Field>
                    <Field label="Article">
                        <textarea
                            className="ls-textarea"
                            rows={22}
                            value={article.body}
                            onChange={(e) => setArticle({ ...article, body: e.target.value })}
                            style={{ fontSize: 14 }}
                        />
                    </Field>
                </div>
            )}
        </div>
    );
};

export default BlogScreen;
